import { atom, selector } from "recoil";
import {
  FALLBACK_ROOM_NAME,
  getChatRoomDisplayName,
  getChatRoomId,
} from "../chatRoomUtils";

/** Socket connection lifecycle: connected | connecting | disconnected */
export const CHAT_SOCKET_STATUS = {
  CONNECTED: "connected",
  CONNECTING: "connecting",
  DISCONNECTED: "disconnected",
};

export const chatSocketConnectedState = atom({
  key: "chatSocketConnectedState",
  default: CHAT_SOCKET_STATUS.DISCONNECTED,
});

/** True only while the Communication page (ChatContainer) is mounted. */
export const chatCommunicationPageActiveState = atom({
  key: "chatCommunicationPageActiveState",
  default: false,
});

/** Per-room unread counts updated via global user topic events. */
export const chatUnreadCountsState = atom({
  key: "chatUnreadCountsState",
  default: {},
});

/** Recent message notifications for global awareness outside the chat page. */
export const chatNotificationsState = atom({
  key: "chatNotificationsState",
  default: [],
});

/** Typing users keyed by roomId: { [roomId]: [{ userId, userName, expiresAt }] } */
export const chatTypingUsersState = atom({
  key: "chatTypingUsersState",
  default: {},
});

/** Sum of all room unread counts — used for sidebar communication badge. */
export const totalChatUnreadCountState = selector({
  key: "totalChatUnreadCountState",
  get: ({ get }) => {
    const counts = get(chatUnreadCountsState);
    return Object.values(counts).reduce(
      (total, count) => total + Math.max(0, Number(count) || 0),
      0,
    );
  },
});

export const chatRoomsState = atom({
  key: "chatRoomsState",
  default: [],
});

export const chatCurrentUserIdState = atom({
  key: "chatCurrentUserIdState",
  default: null,
});

export const selectedChatRoomIdState = atom({
  key: "selectedChatRoomIdState",
  default: null,
});

export const chatComposerTextState = atom({
  key: "chatComposerTextState",
  default: "",
});

export const chatEditingMessageIdState = atom({
  key: "chatEditingMessageIdState",
  default: null,
});

export const chatMessagesByRoomState = atom({
  key: "chatMessagesByRoomState",
  default: {},
});

export const chatMessagesRefreshState = atom({
  key: "chatMessagesRefreshState",
  default: 0,
});

export const sortedChatRoomsState = selector({
  key: "sortedChatRoomsState",
  get: ({ get }) => {
    const rooms = get(chatRoomsState);

    return [...rooms].sort((left, right) => (right?.updatedAt || 0) - (left?.updatedAt || 0));
  },
});

export const selectedChatRoomState = selector({
  key: "selectedChatRoomState",
  get: ({ get }) => {
    const selectedRoomId = get(selectedChatRoomIdState);
    const rooms = get(sortedChatRoomsState);

    if (!selectedRoomId) {
      return null;
    }

    return rooms.find((room) => getChatRoomId(room) === String(selectedRoomId)) || null;
  },
});

export const selectedChatRoomNameState = selector({
  key: "selectedChatRoomNameState",
  get: ({ get }) => {
    const selectedRoom = get(selectedChatRoomState);
    const currentUserIdentifier = get(chatCurrentUserIdState);

    return getChatRoomDisplayName(selectedRoom, currentUserIdentifier) || FALLBACK_ROOM_NAME;
  },
});

export const selectedChatMessagesState = selector({
  key: "selectedChatMessagesState",
  get: ({ get }) => {
    const selectedRoomId = get(selectedChatRoomIdState);
    const messagesByRoom = get(chatMessagesByRoomState);

    if (!selectedRoomId) {
      return [];
    }

    return messagesByRoom[String(selectedRoomId)] || [];
  },
});

export const selectedChatTypingUsersState = selector({
  key: "selectedChatTypingUsersState",
  get: ({ get }) => {
    const selectedRoomId = get(selectedChatRoomIdState);
    if (!selectedRoomId) {
      return [];
    }

    const typingByRoom = get(chatTypingUsersState);
    const typers = typingByRoom[String(selectedRoomId)] || [];
    const now = Date.now();

    return typers.filter((item) => !item.expiresAt || item.expiresAt > now);
  },
});
